import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowRight, CheckCircle2, Package, DollarSign, Truck, Receipt, RotateCcw, Phone } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { DriverCodSettlement } from '@/types/financial.types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SettlementTypeBadge } from '@/components/features/financials/shared/SettlementTypeBadge';
import { useI18n } from '@/contexts/I18nContext';

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-border',
  confirmed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  paid: 'bg-green-500/10 text-green-600 border-green-500/20',
  reversed: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export default function DriverSettlementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { get } = useApi();
  const { patch, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [settlement, setSettlement] = useState<DriverCodSettlement | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const STATUS_LABEL: Record<string, string> = {
    draft: t.financials.status_draft,
    confirmed: t.financials.status_confirmed,
    paid: t.financials.status_paid,
    reversed: t.financials.status_reversed,
  };

  useEffect(() => {
    if (id) fetchSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSettlement = async () => {
    setHasFetched(false);
    try {
      const result: any = await get(`/api/v1/driver-cod-settlements/${id}`);
      const data = result?.success !== undefined ? result?.data : result;
      setSettlement(data);
    } catch {
      toast.error('فشل في تحميل تفاصيل التسوية');
    } finally {
      setHasFetched(true);
    }
  };

  const handleConfirm = async () => {
    try {
      await patch(`/api/v1/driver-cod-settlements/${id}/confirm`, {});
      toast.success(t.financials.settlement_confirmed);
      fetchSettlement();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في تأكيد التسوية');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await patch(`/api/v1/driver-cod-settlements/${id}/pay`, {});
      toast.success(t.financials.settlement_paid);
      fetchSettlement();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في تحديث حالة التسوية');
    }
  };

  const handleReverse = async () => {
    try {
      await patch(`/api/v1/driver-cod-settlements/${id}/reverse`, {});
      toast.success(t.financials.settlement_reversed);
      fetchSettlement();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في إلغاء التسوية');
    }
  };

  if (!hasFetched) return <LoadingSkeleton type="card" rows={4} />;
  if (!settlement) return null;

  const items = settlement.items ?? [];
  const totalShipmentValue = items.reduce((s, r) => s + Number(r.shipment_value), 0);
  const totalDeliveryFee = items.reduce((s, r) => s + Number(r.delivery_fee), 0);
  const totalAmount = items.reduce((s, r) => s + Number(r.total_amount), 0);

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              تفاصيل التسوية
              <SettlementTypeBadge type="cod" />
            </h1>
            <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-3">
              <span>السائق: {settlement.driver_name}</span>
              {settlement.driver_phone && (
                <a
                  href={`tel:${settlement.driver_phone}`}
                  className="flex items-center gap-1 text-primary hover:underline"
                  dir="ltr"
                >
                  <Phone className="h-3 w-3" />
                  {settlement.driver_phone}
                </a>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard/driver-cod-settlements')}
            className="gap-1 self-start sm:self-auto"
          >
            <ArrowRight className="h-4 w-4" />
            رجوع
          </Button>
        </div>
      </div>

      <div className="space-y-6" dir="rtl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-xs text-muted-foreground">قيمة الشحنات</span>
              </div>
              <div className="text-xl font-bold text-green-600">{formatCurrency(settlement.shipment_value)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-muted-foreground">رسوم التوصيل</span>
              </div>
              <div className="text-xl font-bold text-blue-600">{formatCurrency(settlement.delivery_fee)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground">{t.financials.total_amount}</span>
              </div>
              <div className="text-xl font-bold">{formatCurrency(settlement.total_amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">عدد الشحنات</span>
              </div>
              <div className="text-xl font-bold">{items.length}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">معلومات التسوية</CardTitle>
              <div className="flex items-center gap-2">
                <SettlementTypeBadge type="cod" />
                <Badge className={cn('border text-xs', STATUS_COLOR[settlement.status])}>
                  {STATUS_LABEL[settlement.status] ?? settlement.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">السائق:</span>
                <span className="me-2 font-medium">{settlement.driver_name}</span>
              </div>
              {settlement.driver_phone && (
                <div>
                  <span className="text-muted-foreground">الهاتف:</span>
                  <a
                    href={`tel:${settlement.driver_phone}`}
                    className="me-2 font-medium text-primary hover:underline"
                    dir="ltr"
                  >
                    {settlement.driver_phone}
                  </a>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">تاريخ الإنشاء:</span>
                <span className="me-2">{formatDate(settlement.created_at)}</span>
              </div>
              {settlement.confirmed_at && (
                <div>
                  <span className="text-muted-foreground">تاريخ التأكيد:</span>
                  <span className="me-2">{formatDate(settlement.confirmed_at)}</span>
                </div>
              )}
              {settlement.notes && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">ملاحظات:</span>
                  <span className="me-2">{settlement.notes}</span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {settlement.status === 'draft' && (
                <Button onClick={handleConfirm} disabled={loading} className="gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  {t.financials.confirm_settlement}
                </Button>
              )}
              {settlement.status === 'confirmed' && (
                <Button onClick={handleMarkPaid} disabled={loading} className="gap-1.5 bg-green-600 hover:bg-green-700">
                  <DollarSign className="h-4 w-4" />
                  {t.financials.mark_paid}
                </Button>
              )}
              {settlement.status === 'draft' && (
                <Button
                  onClick={handleReverse}
                  disabled={loading}
                  variant="outline"
                  className="gap-1.5 border-red-500 text-red-600 hover:bg-red-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  {t.financials.reverse_settlement}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              الشحنات المشمولة ({items.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">لا توجد شحنات</p>
            ) : (
              <div className="rounded-b-lg overflow-hidden">
                <Table dir="rtl">
                  <TableHeader>
                    <TableRow className="h-10 bg-muted/50">
                      <TableHead className="text-end text-xs font-semibold px-4">رقم الرحلة</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">رقم التتبع</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">المستلم</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">الإجمالي</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">رسوم التوصيل</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">قيمة الشحنة</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow
                        key={item.id}
                        className="h-10 cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate(`/dashboard/shipments/${item.shipment_id}`)}
                      >
                        <TableCell className="px-4 py-2 font-mono text-xs text-muted-foreground">
                          {item.trip_number ?? '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 font-mono text-sm font-medium">
                          {item.tracking_number ?? item.shipment_id}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-muted-foreground">
                          {item.recipient_name ?? '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm font-bold">
                          {formatCurrency(item.total_amount)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-blue-600">
                          {formatCurrency(item.delivery_fee)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-green-600 font-medium">
                          {formatCurrency(item.shipment_value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="bg-muted/60 font-semibold border-t-2">
                      <TableCell className="px-4 py-3 text-sm font-bold" colSpan={3}>الإجمالي</TableCell>
                      <TableCell className="px-4 py-3 text-sm font-bold">{formatCurrency(totalAmount)}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-blue-600 font-bold">{formatCurrency(totalDeliveryFee)}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-green-600 font-bold">{formatCurrency(totalShipmentValue)}</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
